import type { TimeOffStatus, TimeOffType } from '../../../api/timeOff';
import type { TimeOffEntryCreateDraft } from './timeOffEntryTypes';
import { defaultPartialDayRange, localMidnight, nextAllDayEnd, toApiIso } from './timeOffDateUtils';

export type SchedulerViewType = 'day' | 'week' | 'workWeek' | 'month' | string;

export interface SchedulerCellRange {
  startDate: Date;
  endDate: Date;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isAllDaySchedulerCell(currentView: SchedulerViewType, start: Date, end: Date): boolean {
  if (currentView === 'month') return true;

  const startAtMidnight =
    start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0;
  const endAtMidnight = end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0;
  const durationMs = end.getTime() - start.getTime();

  return startAtMidnight && endAtMidnight && durationMs >= MS_PER_DAY;
}

function timedRangeFromCell(start: Date, end: Date): { startLocal: Date; endLocal: Date } {
  const durationMs = end.getTime() - start.getTime();
  if (durationMs > 0) {
    return { startLocal: new Date(start), endLocal: new Date(end) };
  }
  const endLocal = new Date(start);
  endLocal.setHours(endLocal.getHours() + 1);
  return { startLocal: new Date(start), endLocal };
}

/** Default draft when Add is clicked without a calendar cell (today, view-aware). */
export function buildDefaultCreateDraft(
  currentView: SchedulerViewType,
  calendarTargetIds: string[] = []
): TimeOffEntryCreateDraft {
  const today = new Date();
  if (currentView === 'month') {
    const startLocal = localMidnight(today);
    return {
      employeeUpn: '',
      employeeDisplayName: null,
      type: 'Personal',
      status: 'Approved',
      startLocal,
      endLocal: nextAllDayEnd(startLocal),
      isAllDay: true,
      notes: null,
      calendarTargetIds: [...calendarTargetIds]
    };
  }

  const { start, end } = defaultPartialDayRange(today);
  return {
    employeeUpn: '',
    employeeDisplayName: null,
    type: 'Personal',
    status: 'Approved',
    startLocal: start,
    endLocal: end,
    isAllDay: false,
    notes: null,
    calendarTargetIds: [...calendarTargetIds]
  };
}

export function buildCreateDraftFromSchedulerCell(
  currentView: SchedulerViewType,
  cell: SchedulerCellRange,
  calendarTargetIds: string[] = []
): TimeOffEntryCreateDraft {
  const start = cell.startDate instanceof Date ? cell.startDate : new Date(cell.startDate);
  const end = cell.endDate instanceof Date ? cell.endDate : new Date(cell.endDate);

  if (isAllDaySchedulerCell(currentView, start, end)) {
    const startLocal = localMidnight(start);
    return {
      employeeUpn: '',
      employeeDisplayName: null,
      type: 'Personal',
      status: 'Approved',
      startLocal,
      endLocal: nextAllDayEnd(startLocal),
      isAllDay: true,
      notes: null,
      calendarTargetIds: [...calendarTargetIds]
    };
  }

  const { startLocal, endLocal } = timedRangeFromCell(start, end);
  return {
    employeeUpn: '',
    employeeDisplayName: null,
    type: 'Personal',
    status: 'Approved',
    startLocal,
    endLocal,
    isAllDay: false,
    notes: null,
    calendarTargetIds: [...calendarTargetIds]
  };
}

export function createDraftToApiPayload(draft: TimeOffEntryCreateDraft) {
  const allDayEnd = nextAllDayEnd(draft.startLocal);
  return {
    employeeUpn: draft.employeeUpn.trim(),
    employeeDisplayName: draft.employeeDisplayName,
    type: draft.type as TimeOffType,
    status: draft.status as TimeOffStatus,
    startUtc: toApiIso(draft.startLocal, draft.isAllDay),
    endUtc: toApiIso(draft.isAllDay ? allDayEnd : draft.endLocal, draft.isAllDay),
    isAllDay: draft.isAllDay,
    notes: draft.notes,
    calendarTargetIds: draft.calendarTargetIds
  };
}
